/// <reference types="vite/client" />
import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import archivoLatinUrl from '@/assets/fonts/archivo-display-latin.woff2?url';
import plexLatinUrl from '@fontsource-variable/ibm-plex-sans/files/ibm-plex-sans-latin-wght-normal.woff2?url';
import { ThemeProvider } from 'next-themes';

import { Link } from '@/core/i18n/navigation';
import { DEFAULT_ADSENSE_CODE, envConfigs } from '@/config';
import { NO_ADS_PATH } from '@/lib/ads';
import { getQueryClient } from '@/lib/query-client';
import { cn } from '@/lib/utils';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { Ads } from '@/components/analytics/ads';
import { Clarity } from '@/components/analytics/clarity';
import { GoogleAnalytics } from '@/components/analytics/google-analytics';
import { PageViewTracker } from '@/components/analytics/page-view-tracker';
import { Plausible } from '@/components/analytics/plausible';
import { PurchaseTracker } from '@/components/analytics/purchase-tracker';
import { CustomerService } from '@/components/customer-service';
import { GoogleOneTap } from '@/components/google-one-tap';
import { SandboxPreviewBridge } from '@/components/sandbox-preview-bridge';
import { buttonVariants } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';

import '@/styles/fonts.css';
import '@/styles/globals.css';

// Analytics IDs live in the DB config (1h-cached service). Fetched via a
// server function so drizzle/db code never reaches the client bundle.
const getAnalyticsConfigs = createServerFn().handler(async () => {
  const { getAllConfigs } = await import('@/modules/config/service');
  const { getStoredFooterBadges } =
    await import('@/modules/footer-badges/service');
  const [configs, storedFooterBadges] = await Promise.all([
    getAllConfigs(),
    getStoredFooterBadges().catch(() => {
      console.warn('[footer-badges] unavailable; rendering without badges');
      return undefined;
    }),
  ]);
  return {
    footerBadges: storedFooterBadges ?? null,
    gaId: configs.google_analytics_id?.trim() || '',
    plausibleDomain: configs.plausible_domain?.trim() || '',
    plausibleSrc: configs.plausible_src?.trim() || '',
    clarityProjectId: configs.clarity_project_id?.trim() || '',
    adsenseCode: configs.adsense_code?.trim() || DEFAULT_ADSENSE_CODE,
    crispWebsiteId:
      configs.crisp_enabled === 'true'
        ? configs.crisp_website_id?.trim() || ''
        : '',
    tawkPropertyId:
      configs.tawk_enabled === 'true'
        ? configs.tawk_property_id?.trim() || ''
        : '',
    tawkWidgetId:
      configs.tawk_enabled === 'true'
        ? configs.tawk_widget_id?.trim() || ''
        : '',
  };
});

export const Route = createRootRoute({
  loader: () => getAnalyticsConfigs(),
  head: () => {
    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { title: envConfigs.app_name },
        { name: 'description', content: envConfigs.app_description },
      ],
      links: [
        // Preload the latin display/body fonts (declared in styles/fonts.css
        // with font-display: swap) so they make the first paint.
        {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: archivoLatinUrl,
          crossOrigin: 'anonymous',
        },
        {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: plexLatinUrl,
          crossOrigin: 'anonymous',
        },
        { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
        { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      ],
    };
  },
  component: RootComponent,
  shellComponent: RootDocument,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootComponent() {
  const analytics = Route.useLoaderData();

  return (
    <QueryClientProvider client={getQueryClient()}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        disableTransitionOnChange
      >
        <Outlet />
        <SandboxPreviewBridge />
        <Toaster position="top-center" richColors />
        <GoogleOneTap />
        <PurchaseTracker />
        <PageViewTracker />
        {analytics?.gaId ? (
          <GoogleAnalytics measurementId={analytics.gaId} />
        ) : null}
        {analytics?.plausibleDomain || analytics?.plausibleSrc ? (
          <Plausible
            domain={analytics.plausibleDomain}
            src={analytics.plausibleSrc || undefined}
          />
        ) : null}
        {analytics?.clarityProjectId ? (
          <Clarity projectId={analytics.clarityProjectId} />
        ) : null}
        <CustomerService
          crispWebsiteId={analytics?.crispWebsiteId || undefined}
          tawkPropertyId={analytics?.tawkPropertyId || undefined}
          tawkWidgetId={analytics?.tawkWidgetId || undefined}
        />
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  const analytics = Route.useLoaderData();
  const adsenseCode = analytics?.adsenseCode || DEFAULT_ADSENSE_CODE;
  // 404 and error pages keep the account <meta> (site verification) but skip
  // the ad loader, so Auto ads never fill a page without content.
  const isNotFound = useRouterState({
    select: (s) =>
      s.matches.some(
        (match) => match.status === 'notFound' || match.globalNotFound
      ),
  });
  const isError = useRouterState({
    select: (s) => s.matches.some((match) => match.status === 'error'),
  });
  const isNoAdsPath = useRouterState({
    select: (s) => NO_ADS_PATH.test(s.location.pathname),
  });
  const loadAds = !isNotFound && !isError && !isNoAdsPath;

  return (
    <html lang={getLocale()} suppressHydrationWarning>
      <head>
        <HeadContent />
        {isNotFound && <meta name="robots" content="noindex" />}
        {loadAds ? (
          <Ads code={adsenseCode} />
        ) : (
          <meta name="google-adsense-account" content={adsenseCode} />
        )}
      </head>
      <body className="font-sans antialiased">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function NotFound() {
  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="text-6xl font-bold">404</h1>
      <p className="text-muted-foreground">{m['common.not_found.message']()}</p>
      <Link href="/" className={cn(buttonVariants(), 'mt-2')}>
        {m['common.not_found.back_home']()}
      </Link>
    </div>
  );
}

function RootError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center gap-4">
      <h1 className="text-4xl font-bold">{m['common.error.title']()}</h1>
      <p className="text-muted-foreground">{m['common.error.message']()}</p>
      {import.meta.env.DEV && error instanceof Error && (
        <pre className="bg-muted mt-2 max-w-lg overflow-auto rounded p-4 text-xs">
          {error.message}
        </pre>
      )}
      <button
        type="button"
        onClick={reset}
        className={cn(buttonVariants(), 'mt-2')}
      >
        {m['common.error.retry']()}
      </button>
    </div>
  );
}
